import { useState } from 'react'
import Card from '../../../components/common/Card/Card'
import Badge from '../../../components/common/Badge/Badge'
import Button from '../../../components/common/Button/Button'
import {
  formatRequestDate,
  formatTimestamp,
  getStatusBadgeVariant
} from '../../../services/timeOffService'

/**
 * RequestCard Component
 * Display individual time off request with status and actions
 *
 * @param {object} request - Request data
 * @param {boolean} isAdmin - Whether current user is admin
 * @param {boolean} isOwnRequest - Whether this is the current user's request
 * @param {function} onApprove - Approve callback (admin only)
 * @param {function} onReject - Reject callback (admin only)
 * @param {function} onCancel - Cancel callback (employee only)
 */
const RequestCard = ({
  request,
  isAdmin = false,
  isOwnRequest = false,
  onApprove,
  onReject,
  onCancel
}) => {
  const [showFullReason, setShowFullReason] = useState(false)

  const isPending = request.status === 'pending'
  const isApproved = request.status === 'approved'
  const isRejected = request.status === 'rejected'
  const isCancelled = request.status === 'cancelled'

  // Determine if actions should be shown
  const showAdminActions = isAdmin && isPending
  const showCancelAction = isOwnRequest && isPending

  const toggleReason = () => {
    setShowFullReason(!showFullReason)
  }

  const handleApprove = () => {
    if (window.confirm('Approve this time off request?')) {
      onApprove(request.id)
    }
  }

  const handleReject = () => {
    onReject(request)
  }

  const handleCancel = () => {
    if (window.confirm('Cancel this time off request?')) {
      onCancel(request.id)
    }
  }

  return (
    <Card style={{
      border: isPending ? '2px solid var(--psyber-warning)' : undefined,
      background: isPending ? 'rgba(255, 193, 7, 0.03)' : undefined
    }}>
      {/* Header: Employee Info (Admin View) */}
      {isAdmin && (
        <div style={{
          marginBottom: '1rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid var(--psyber-card-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <i className="fas fa-user" style={{ color: 'var(--psyber-text-secondary)' }}></i>
            <strong style={{ fontSize: 'var(--psyber-font-size-base)', color: 'var(--psyber-text)' }}>
              {request.employeeName}
            </strong>
          </div>
          <div style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-text-secondary)', marginLeft: '1.5rem' }}>
            ID: {request.employeeId}
          </div>
        </div>
      )}

      {/* Leave Type and Status Badges */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginBottom: '1rem'
      }}>
        <Badge variant="info" icon="fas fa-calendar-check">
          {request.leaveType}
        </Badge>
        <Badge variant={getStatusBadgeVariant(request.status)}>
          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
        </Badge>
        <Badge variant="info" icon="fas fa-clock">
          {request.duration} day{request.duration !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Dates */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: 'var(--psyber-font-size-base)',
          color: 'var(--psyber-text)'
        }}>
          <i className="fas fa-calendar-days" style={{ color: 'var(--psyber-primary)' }}></i>
          <strong>{formatRequestDate(request.startDate, request.endDate)}</strong>
        </div>
      </div>

      {/* Reason */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{
          fontSize: 'var(--psyber-font-size-sm)',
          fontWeight: 600,
          color: 'var(--psyber-text-secondary)',
          marginBottom: '0.25rem'
        }}>
          Reason:
        </div>
        <div style={{
          fontSize: 'var(--psyber-font-size-sm)',
          color: 'var(--psyber-text)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap'
        }}>
          {showFullReason || request.reason.length <= 150
            ? request.reason
            : `${request.reason.substring(0, 150)}...`}
        </div>
        {request.reason.length > 150 && (
          <button
            onClick={toggleReason}
            style={{
              marginTop: '0.25rem',
              background: 'none',
              border: 'none',
              color: 'var(--psyber-primary)',
              fontSize: 'var(--psyber-font-size-sm)',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline'
            }}
          >
            {showFullReason ? 'Show Less' : 'Read More'}
          </button>
        )}
      </div>

      {/* Review Comments (if rejected) */}
      {isRejected && request.reviewComments && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem',
          background: 'var(--psyber-error-bg)',
          border: '1px solid var(--psyber-error)',
          borderRadius: 'var(--psyber-radius-sm)'
        }}>
          <div style={{
            fontSize: 'var(--psyber-font-size-sm)',
            fontWeight: 600,
            color: 'var(--psyber-error)',
            marginBottom: '0.25rem'
          }}>
            Rejection Reason:
          </div>
          <div style={{
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text)',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap'
          }}>
            {request.reviewComments}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div style={{
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--psyber-card-border)',
        fontSize: 'var(--psyber-font-size-xs)',
        color: 'var(--psyber-text-muted)'
      }}>
        <div style={{ marginBottom: '0.25rem' }}>
          <i className="fas fa-clock"></i> Submitted: {formatTimestamp(request.createdAt)}
        </div>
        {(isApproved || isRejected) && request.reviewedAt && (
          <div>
            <i className="fas fa-user-check"></i> Reviewed by {request.reviewedByName} on {formatTimestamp(request.reviewedAt)}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {(showAdminActions || showCancelAction) && (
        <div style={{
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--psyber-card-border)',
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap'
        }}>
          {/* Admin Actions */}
          {showAdminActions && (
            <>
              <Button
                variant="primary"
                icon="fas fa-check"
                onClick={handleApprove}
                style={{ flex: 1 }}
              >
                Approve
              </Button>
              <Button
                variant="ghost"
                icon="fas fa-times"
                onClick={handleReject}
                style={{ flex: 1 }}
              >
                Reject
              </Button>
            </>
          )}

          {/* Employee Cancel Action */}
          {showCancelAction && (
            <Button
              variant="ghost"
              icon="fas fa-ban"
              onClick={handleCancel}
              style={{ width: '100%' }}
            >
              Cancel Request
            </Button>
          )}
        </div>
      )}
    </Card>
  )
}

export default RequestCard
